import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PortalShell from "@/portal/PortalShell";
import { usePortal } from "@/portal/PortalContext";
import { DecisionRow, DecisionDetail, ConfirmPanel } from "@/portal/DecisionCard";
import { PageHeader, EmptyState, ErrorState, SkeletonRows } from "@/portal/ui/Layout";
import { Segmented } from "@/portal/ui/Tabs";
import { ClipboardCheck } from "lucide-react";
import { pluralize } from "@/portal/format";

export default function ClientDecisions() {
  const { overview, refresh: refreshOverview } = usePortal();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState("pending");
  const [selectedId, setSelectedId] = useState(params.get("id") || null);
  const [intent, setIntent] = useState(null);
  const [comment, setComment] = useState("");

  const load = useCallback(async () => {
    setError(null);
    try { const { data } = await api.get("/client/decisions"); setData(data); } catch (e) { setError(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const all = useMemo(() => [...(data?.pending || []), ...(data?.resolved || [])], [data]);
  const items = (view === "pending" ? data?.pending : data?.resolved) || [];
  const selected = all.find((a) => a.id === selectedId) || items[0] || null;

  useEffect(() => {
    if (!data) return;
    if (selected && view === "pending" && selected.status !== "pending") setView("resolved");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  useEffect(() => { setIntent(null); setComment(""); }, [selected?.id]);

  const select = (a) => { setSelectedId(a.id); const next = new URLSearchParams(params); next.set("id", a.id); setParams(next, { replace: true }); };
  const project = (overview?.projects || []).find((p) => p.id === selected?.project_id);
  const overdue = (data?.pending || []).filter((a) => a.deadline && a.deadline.slice(0, 10) < new Date().toISOString().slice(0, 10)).length;

  async function done() { await load(); refreshOverview(); setIntent(null); setComment(""); setView("resolved"); }

  return (
    <PortalShell title="Decizii" crumbs={[{ label: "Acasă", to: "/portal" }, { label: "Decizii & aprobări" }]}>
      {error && !data ? <ErrorState onRetry={load} /> : !data ? <SkeletonRows rows={3} height="h-28" /> : (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.3fr)_minmax(0,0.7fr)] gap-5 items-start">
          <div>
            <PageHeader size="md" title="Decizii & aprobări" className="!mb-4"
              description={data.pending.length ? `${pluralize(data.pending.length, "solicitare așteaptă", "solicitări așteaptă")} decizia ta${overdue ? ` · ${overdue} cu termen depășit` : ""}.` : "Nu ai solicitări în așteptare."} />
            <Segmented ariaLabel="Stare" value={view} onChange={(v) => { setView(v); setSelectedId(null); }} className="mb-4"
              options={[{ value: "pending", label: "În așteptare", count: data.pending.length }, { value: "resolved", label: "Istoric", count: data.resolved.length }]} />
            {items.length === 0 ? (
              <EmptyState compact icon={ClipboardCheck} testid="decisions-empty"
                title={view === "pending" ? "Nicio decizie în așteptare" : "Nicio decizie în istoric"}
                description={view === "pending" ? "Când echipa Brisk are nevoie de aprobarea ta, solicitarea va apărea aici." : "Deciziile aprobate, respinse sau confirmate vor fi arhivate aici."} />
            ) : (
              <div className="space-y-3" data-testid="decisions-list">
                {items.map((a) => <DecisionRow key={a.id} a={a} selected={selected?.id === a.id} onSelect={() => select(a)} />)}
              </div>
            )}
          </div>

          {selected ? (
            <>
              <DecisionDetail a={selected} pm={project?.pm} comment={comment} setComment={setComment} intent={intent} onIntent={(i) => setIntent((cur) => (cur === i ? null : i))} />
              <ConfirmPanel a={selected} project={project} intent={intent} comment={comment} onBack={() => setIntent(null)} onDone={done} />
            </>
          ) : (
            <div className="xl:col-span-2 cp-panel p-10 text-center text-cp-text-2 text-[14px]" data-testid="decision-placeholder">Selectează o solicitare din listă pentru a vedea detaliile.</div>
          )}
        </div>
      )}
    </PortalShell>
  );
}
